/**
 * TemplateCard.tsx
 *
 * The card used by every grid under Marketing → Templates (email templates, SMS
 * templates, email components). Lives in its own module so the tabs can share it
 * without importing each other.
 */
import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Pencil, Trash2, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface TemplateCardProps {
  name: string;
  /** Folder pill. Omitted for entities that aren't foldered (e.g. Email Components). */
  folderName?: string;
  active: boolean;
  preview: React.ReactNode;
  onEdit: () => void;
  onDuplicate?: () => void;
  onDelete: () => void;
  meta?: React.ReactNode;
  /**
   * Extra hover actions, rendered before Delete. Used by Email Components for
   * "Details" (rename / describe / enable), which the foldered tabs handle in
   * their own builder pages instead.
   */
  extraActions?: Array<{ icon: LucideIcon; title: string; onClick: () => void }>;
}

export function TemplateCard({
  name,
  folderName,
  active,
  preview,
  onEdit,
  onDuplicate,
  onDelete,
  meta,
  extraActions,
}: TemplateCardProps) {
  return (
    <div
      className="group relative bg-white border border-slate-200 rounded-xl overflow-hidden cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5"
      onClick={onEdit}
    >
      {/* Preview area */}
      <div className="h-44 bg-slate-50 overflow-hidden relative">{preview}</div>

      {/* Info */}
      <div className="p-4 border-t border-slate-100">
        <div className="font-semibold text-sm text-slate-900 truncate">{name}</div>
        {meta && <div className="text-xs text-slate-500 mt-1 truncate">{meta}</div>}
        <div
          className={cn(
            'flex items-center mt-2 text-xs text-slate-500',
            folderName ? 'justify-between' : 'justify-end',
          )}
        >
          {folderName && (
            <span className="inline-block px-2 py-0.5 rounded bg-blue-50 text-blue-700 font-semibold capitalize">
              {folderName}
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <span
              className={cn(
                'w-2 h-2 rounded-full inline-block',
                active ? 'bg-green-500' : 'bg-red-400',
              )}
            />
            {active ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      {/* Hover actions */}
      <div className="absolute top-2.5 right-2.5 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          className="w-8 h-8 rounded-full bg-white/95 shadow flex items-center justify-center text-slate-500 hover:text-primary hover:scale-110 transition-all"
          title="Edit"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        {onDuplicate && (
          <button
            className="w-8 h-8 rounded-full bg-white/95 shadow flex items-center justify-center text-slate-500 hover:text-slate-700 hover:scale-110 transition-all"
            title="Duplicate"
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
        )}
        {extraActions?.map(({ icon: Icon, title, onClick }) => (
          <button
            key={title}
            className="w-8 h-8 rounded-full bg-white/95 shadow flex items-center justify-center text-slate-500 hover:text-slate-700 hover:scale-110 transition-all"
            title={title}
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          >
            <Icon className="h-3.5 w-3.5" />
          </button>
        ))}
        <button
          className="w-8 h-8 rounded-full bg-white/95 shadow flex items-center justify-center text-slate-500 hover:text-red-500 hover:scale-110 transition-all"
          title="Delete"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
